import type { Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";
import {
  CommercialMeterKey,
  parseCommercialUsageLimitRejection,
  type CommercialUsageLimitRejection,
} from "@/modules/commercial-plans";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";

/**
 * Checks the authoritative Voice Mode gate for a fresh recording.
 *
 * Only the endpoint's typed, rejected 429 blocks capture. Admission service
 * failures remain fail-open so an outage cannot prevent someone from recording.
 */
export const getVoiceModeStartRejection = async ({
  audioRecordingId,
}: {
  audioRecordingId: Uuid;
}): Promise<CommercialUsageLimitRejection | null> => {
  try {
    const response = await api.post("/v2/audio-recordings/{audio_recording_id}/admit-start", {
      params: {
        path: { audio_recording_id: audioRecordingId },
      },
    });

    if (!response.error) {
      return null;
    }

    const rejection = parseCommercialUsageLimitRejection(response.error);
    if (
      response.response?.status === 429 &&
      rejection?.accepted === false &&
      rejection.meterKey === CommercialMeterKey.VoiceModeHours
    ) {
      return rejection;
    }

    logger.warn({
      message: "[VoiceStartAdmission] Ignoring an unavailable or malformed admission response",
      info: {
        audioRecordingId,
        responseStatus: response.response?.status ?? null,
        error: objectModule.safeAsJson(response.error),
      },
    });
    return null;
  } catch (error) {
    logger.warn({
      message: "[VoiceStartAdmission] Admission request failed; allowing recording start",
      info: {
        audioRecordingId,
        error: objectModule.safeErrorAsJson(error as Error),
      },
    });
    return null;
  }
};
